import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Calendar } from '../../components/ui/calendar';
import { toast } from 'sonner';
import { 
  CalendarIcon, 
  Plus, 
  Pencil, 
  Trash2, 
  Check, 
  ChevronsUpDown, 
  TrendingUp,
  TrendingDown,
  Banknote,
  FileText,
  CreditCard,
  Upload,
  CheckCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  X,
  Bell,
  List,
  AlertCircle
} from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { cn } from '../../lib/utils';
import InfoBanner from '../../components/ui/info-banner';
import { useHolidays } from '../../hooks/useHolidays';

// Timezone-safe date formatting (local date to YYYY-MM-DD)
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const AccountingCalendar = () => {
  const [events, setEvents] = useState([]);
  const [takipOdemeleri, setTakipOdemeleri] = useState([]);
  const [hatirlaticilar, setHatirlaticilar] = useState([]);
  const [hatirlaticiEvents, setHatirlaticiEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const { getHolidayForDate } = useHolidays(currentMonth);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  
  // Hatırlatıcı Modal States
  const [showHatirlaticiModal, setShowHatirlaticiModal] = useState(false);
  const [showHatirlaticiListModal, setShowHatirlaticiListModal] = useState(false);
  const [editingHatirlatici, setEditingHatirlatici] = useState(null);
  const [hatirlaticiForm, setHatirlaticiForm] = useState({
    baslik: '',
    aciklama: '',
    yillar: [],
    aylar: [],
    gunler: [],
    gun_tipi: 'belirli',
    hafta_gunleri: [],
    belgeler: [],
    renk: '#8B5CF6'
  });
  
  // Dropdown data
  const [cariHesaplar, setCariHesaplar] = useState([]);
  const [hesapKartlari, setHesapKartlari] = useState([]);
  const [branches, setBranches] = useState([]);
  
  // Combobox states
  const [cariOpen, setCariOpen] = useState(false);
  const [etiketOpen, setEtiketOpen] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    tipi: 'alacak',
    tarih: '',
    cari_hesap_id: '',
    cari_hesap_adi: '',
    etiket_id: '',
    etiket_adi: '',
    fortis_sube_id: '',
    fortis_sube: '',
    miktar: '',
    odeme_sekli: 'havale_eft',
    aciklama: '',
    belge_url: ''
  });

  const fetchEvents = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const [eventsRes, takipRes, hatirlaticiRes, hatirlaticiEventsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/takvim`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/takvim/takip-odemeleri`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/hatirlaticilar`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/hatirlaticilar/takvim-etkinlikleri`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (eventsRes.ok) setEvents(await eventsRes.json());
      if (takipRes.ok) setTakipOdemeleri(await takipRes.json());
      if (hatirlaticiRes.ok) setHatirlaticilar(await hatirlaticiRes.json());
      if (hatirlaticiEventsRes.ok) setHatirlaticiEvents(await hatirlaticiEventsRes.json());
    } catch (error) {
      console.error('Takvim verileri alınamadı:', error);
    }
  }, []);

  const fetchDropdownData = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [cariRes, etiketRes, branchRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/cari-hesaplar`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/hesap-kartlari`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branches`, { headers })
      ]);

      if (cariRes.ok) setCariHesaplar(await cariRes.json());
      if (etiketRes.ok) setHesapKartlari(await etiketRes.json());
      if (branchRes.ok) setBranches(await branchRes.json());
    } catch (error) {
      console.error('Dropdown verileri alınamadı:', error);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchDropdownData();
  }, [fetchEvents, fetchDropdownData]);

  // Get all events (both manual events and takip payments and reminders)
  const allEvents = [
    ...events,
    ...takipOdemeleri,
    ...hatirlaticiEvents
  ];

  // Get events for a specific date
  const getEventsForDate = (date) => {
    const dateStr = formatDateLocal(date);
    return allEvents.filter(e => e.tarih === dateStr);
  };

  // Get events for selected date
  const selectedDateEvents = getEventsForDate(selectedDate);

  // Check if a date has events
  const hasEvents = (date) => {
    const dateStr = formatDateLocal(date);
    return allEvents.some(e => e.tarih === dateStr);
  };

  // Get event type color
  const getEventColor = (event) => {
    if (event.tamamlandi) return 'bg-gray-400';
    if (event.is_hatirlatici) return event.renk ? '' : 'bg-purple-500';
    if (event.is_kredi_karti) return 'bg-purple-500';
    if (event.is_cek) return 'bg-blue-500';
    return event.tipi === 'alacak' ? 'bg-green-500' : 'bg-red-500';
  };

  const openNewModal = (date = selectedDate) => {
    setEditingEvent(null);
    setForm({
      tipi: 'alacak',
      tarih: formatDateLocal(date),
      cari_hesap_id: '',
      cari_hesap_adi: '',
      etiket_id: '',
      etiket_adi: '',
      fortis_sube_id: '',
      fortis_sube: '',
      miktar: '',
      odeme_sekli: 'havale_eft',
      aciklama: '',
      belge_url: ''
    });
    setShowModal(true);
  };

  const openEditModal = (event) => {
    if (event.is_takip) {
      toast.info('Takip ödemeleri takipler sayfasından düzenlenebilir');
      return;
    }
    if (event.is_kredi_karti) {
      toast.info('Kredi kartı ödemeleri hesaplar sayfasından yönetilir');
      return;
    }
    if (event.is_cek) {
      toast.info('Çek ödemeleri çekler sayfasından düzenlenebilir');
      return;
    }
    setEditingEvent(event);
    setForm({
      tipi: event.tipi || 'alacak',
      tarih: event.tarih || '',
      cari_hesap_id: event.cari_hesap_id || '',
      cari_hesap_adi: event.cari_hesap_adi || '',
      etiket_id: event.etiket_id || '',
      etiket_adi: event.etiket_adi || '',
      fortis_sube_id: event.fortis_sube_id || '',
      fortis_sube: event.fortis_sube || '',
      miktar: event.miktar || '',
      odeme_sekli: event.odeme_sekli || 'havale_eft',
      aciklama: event.aciklama || '',
      belge_url: event.belge_url || ''
    });
    setShowModal(true);
  };

  const handleBelgeUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setForm({ ...form, belge_url: event.target.result });
      toast.success('Belge eklendi');
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!form.tarih || !form.miktar) {
      toast.error('Tarih ve miktar gereklidir');
      return;
    }

    try {
      const token = localStorage.getItem('fortis_token');
      const url = editingEvent
        ? `${process.env.REACT_APP_BACKEND_URL}/api/accounting/takvim/${editingEvent.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/accounting/takvim`;

      const response = await fetch(url, {
        method: editingEvent ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });

      if (response.ok) {
        toast.success(editingEvent ? 'Etkinlik güncellendi' : 'Etkinlik eklendi');
        setShowModal(false);
        fetchEvents();
      } else {
        toast.error('İşlem başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleDelete = async (id, isTakip = false) => {
    if (isTakip) {
      toast.info('Takip ödemeleri takipler sayfasından silinebilir');
      return;
    }
    
    if (!window.confirm('Bu etkinliği silmek istediğinizden emin misiniz?')) return;

    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/takvim/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Etkinlik silindi');
        fetchEvents();
      }
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const handleToggleComplete = async (id, isTakip = false) => {
    if (isTakip) {
      toast.info('Takip ödemeleri için Girişler veya Çıkışlar sayfasını kullanın');
      return;
    }

    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/takvim/${id}/toggle-complete`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.tamamlandi ? 'Etkinlik tamamlandı' : 'Etkinlik aktifleştirildi');
        fetchEvents();
      }
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount || 0);
  };

  const getOdemeSekliLabel = (sekil) => {
    switch (sekil) {
      case 'havale_eft': return 'Havale/EFT';
      case 'cek': return 'Çek';
      case 'nakit': return 'Nakit';
      default: return sekil;
    }
  };

  const getOdemeSekliIcon = (sekil) => {
    switch (sekil) {
      case 'havale_eft': return <Banknote className="h-4 w-4" />;
      case 'cek': return <FileText className="h-4 w-4" />;
      case 'nakit': return <CreditCard className="h-4 w-4" />;
      default: return <Banknote className="h-4 w-4" />;
    }
  };

  // Navigate months
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(today);
    setSelectedDate(today);
  };

  // Hatırlatıcı Functions
  const aylar = [
    { value: 1, label: 'Ocak' },
    { value: 2, label: 'Şubat' },
    { value: 3, label: 'Mart' },
    { value: 4, label: 'Nisan' },
    { value: 5, label: 'Mayıs' },
    { value: 6, label: 'Haziran' },
    { value: 7, label: 'Temmuz' },
    { value: 8, label: 'Ağustos' },
    { value: 9, label: 'Eylül' },
    { value: 10, label: 'Ekim' },
    { value: 11, label: 'Kasım' },
    { value: 12, label: 'Aralık' }
  ];

  const haftaGunleri = [
    { value: 1, label: 'Pazartesi' },
    { value: 2, label: 'Salı' },
    { value: 3, label: 'Çarşamba' },
    { value: 4, label: 'Perşembe' },
    { value: 5, label: 'Cuma' },
    { value: 6, label: 'Cumartesi' },
    { value: 7, label: 'Pazar' }
  ];

  const renkSecenekleri = [
    { value: '#8B5CF6', label: 'Mor' },
    { value: '#3B82F6', label: 'Mavi' },
    { value: '#10B981', label: 'Yeşil' },
    { value: '#F59E0B', label: 'Turuncu' },
    { value: '#EF4444', label: 'Kırmızı' },
    { value: '#EC4899', label: 'Pembe' },
    { value: '#6366F1', label: 'İndigo' },
    { value: '#14B8A6', label: 'Teal' }
  ];

  const currentYear = new Date().getFullYear();
  const yilSecenekleri = [
    { value: 'her_yil', label: 'Her Yıl' },
    ...Array.from({ length: 5 }, (_, i) => ({ 
      value: String(currentYear + i), 
      label: String(currentYear + i) 
    }))
  ];

  const openHatirlaticiModal = (hatirlatici = null) => {
    if (hatirlatici) {
      setEditingHatirlatici(hatirlatici);
      setHatirlaticiForm({
        baslik: hatirlatici.baslik || '',
        aciklama: hatirlatici.aciklama || '',
        yillar: hatirlatici.yillar || [],
        aylar: hatirlatici.aylar || [],
        gunler: hatirlatici.gunler || [],
        gun_tipi: hatirlatici.gun_tipi || 'belirli',
        hafta_gunleri: hatirlatici.hafta_gunleri || [],
        belgeler: hatirlatici.belgeler || [],
        renk: hatirlatici.renk || '#8B5CF6'
      });
    } else {
      setEditingHatirlatici(null);
      setHatirlaticiForm({
        baslik: '',
        aciklama: '',
        yillar: [],
        aylar: ['her_ay'],
        gunler: [],
        gun_tipi: 'belirli',
        hafta_gunleri: [],
        belgeler: [],
        renk: '#8B5CF6'
      });
    }
    setShowHatirlaticiModal(true);
  };

  const handleHatirlaticiSubmit = async () => {
    if (!hatirlaticiForm.baslik) {
      toast.error('Başlık gerekli');
      return;
    }

    if (hatirlaticiForm.gun_tipi === 'belirli' && hatirlaticiForm.gunler.length === 0) {
      toast.error('En az bir gün seçin');
      return;
    }

    if (hatirlaticiForm.gun_tipi === 'haftalik' && hatirlaticiForm.hafta_gunleri.length === 0) {
      toast.error('En az bir hafta günü seçin');
      return;
    }

    try {
      const token = localStorage.getItem('fortis_token');
      const url = editingHatirlatici 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/accounting/hatirlaticilar/${editingHatirlatici.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/accounting/hatirlaticilar`;
      
      const response = await fetch(url, {
        method: editingHatirlatici ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(hatirlaticiForm)
      });

      if (response.ok) {
        toast.success(editingHatirlatici ? 'Hatırlatıcı güncellendi' : 'Hatırlatıcı oluşturuldu');
        setShowHatirlaticiModal(false);
        fetchEvents();
      } else {
        toast.error('İşlem başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleDeleteHatirlatici = async (id) => {
    if (!window.confirm('Bu hatırlatıcıyı silmek istediğinize emin misiniz?')) return;
    
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/hatirlaticilar/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Hatırlatıcı silindi');
        fetchEvents();
      }
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const handleToggleHatirlaticiComplete = async (hatirlaticiId, tarih) => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/hatirlaticilar/${hatirlaticiId}/toggle-complete`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tarih })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.tamamlandi ? 'Hatırlatıcı tamamlandı' : 'Hatırlatıcı aktifleştirildi');
        fetchEvents();
      }
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const toggleArrayValue = (array, value) => {
    if (array.includes(value)) {
      return array.filter(v => v !== value);
    }
    return [...array, value];
  };

  // Get month name in Turkish
  const getMonthName = (date) => {
    return date.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
  };

  // Statistics
  const thisMonthEvents = allEvents.filter(e => {
    const eventDate = new Date(e.tarih);
    return eventDate.getMonth() === currentMonth.getMonth() && 
           eventDate.getFullYear() === currentMonth.getFullYear();
  });
  
  // Bu ayın toplam alacak ve borç
  const alacakTotal = thisMonthEvents.filter(e => e.tipi === 'alacak' && !e.tamamlandi)
    .reduce((sum, e) => sum + (parseFloat(e.miktar) || 0), 0);
  const borcTotal = thisMonthEvents.filter(e => e.tipi === 'borc' && !e.tamamlandi)
    .reduce((sum, e) => sum + (parseFloat(e.miktar) || 0), 0);

  // Bugünden ay sonuna kadar kalan alacak ve borç
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const restOfMonthEvents = thisMonthEvents.filter(e => {
    const eventDate = new Date(e.tarih);
    eventDate.setHours(0, 0, 0, 0);
    return eventDate >= today;
  });
  
  const alacakRestOfMonth = restOfMonthEvents.filter(e => e.tipi === 'alacak' && !e.tamamlandi)
    .reduce((sum, e) => sum + (parseFloat(e.miktar) || 0), 0);
  const borcRestOfMonth = restOfMonthEvents.filter(e => e.tipi === 'borc' && !e.tamamlandi)
    .reduce((sum, e) => sum + (parseFloat(e.miktar) || 0), 0);

  return (
    <div className="space-y-6" data-testid="accounting-calendar">
      {/* Bilgilendirme Notu */}
      <InfoBanner id="accounting-calendar-info" variant="info">
        <strong>Takvim:</strong> Takvimi kullanarak ajandanıza yeni notlar ekleyebilir, hatırlatıcı kurabilir ve bu notlara istediğiniz kişileri ekleyebilirsiniz. 
        <strong>Hatırlatıcı ekle</strong> ile daha karmaşık zaman aralıklarında hatırlatmalar yapabilirsiniz. 
        <strong>Hatırlatıcı listesinden</strong> bu hatırlatmalara ulaşabilirsiniz. 
        Sadece kendi oluşturduğunuz notları silebilirsiniz.
      </InfoBanner>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Takvim</h1>
          <p className="text-gray-500">Muhasebe takvimi ve ödeme takibi</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowHatirlaticiListModal(true)}
            variant="outline"
            className="gap-2"
            data-testid="reminder-list-btn"
          >
            <List className="h-4 w-4" />
            Hatırlatıcı Listesi
          </Button>
          <Button
            onClick={() => openHatirlaticiModal()}
            className="gap-2 bg-purple-600 hover:bg-purple-700"
            data-testid="add-reminder-btn"
          >
            <Bell className="h-4 w-4" />
            Hatırlatıcı Ekle
          </Button>
          <Button
            onClick={() => openNewModal()}
            className="gap-2 bg-indigo-600 hover:bg-indigo-700"
            data-testid="add-event-btn"
          >
            <Plus className="h-4 w-4" />
            Ekle
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-500 rounded-lg">
                <CalendarIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-indigo-600 font-medium">Bu Ay Etkinlik</p>
                <p className="text-2xl font-bold text-indigo-900">{thisMonthEvents.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-4 mb-3">
              <div className="p-3 bg-green-500 rounded-lg">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-green-600 font-medium">Bekleyen Alacak</p>
              </div>
            </div>
            <div className="space-y-1 pl-1">
              <div className="flex justify-between items-center">
                <span className="text-xs text-green-600">Bu Ay Toplamı:</span>
                <span className="text-sm font-bold text-green-900">{formatCurrency(alacakTotal)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-green-600">Bugünden Ay Sonuna:</span>
                <span className="text-sm font-bold text-green-900">{formatCurrency(alacakRestOfMonth)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-4 mb-3">
              <div className="p-3 bg-red-500 rounded-lg">
                <TrendingDown className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-red-600 font-medium">Bekleyen Borç</p>
              </div>
            </div>
            <div className="space-y-1 pl-1">
              <div className="flex justify-between items-center">
                <span className="text-xs text-red-600">Bu Ay Toplamı:</span>
                <span className="text-sm font-bold text-red-900">{formatCurrency(borcTotal)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-red-600">Bugünden Ay Sonuna:</span>
                <span className="text-sm font-bold text-red-900">{formatCurrency(borcRestOfMonth)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-indigo-600" />
                <span className="text-xl">{getMonthName(currentMonth)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentMonth(new Date())}
                  className="h-8 px-3 text-xs"
                >
                  Bugün
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              showOutsideDays={true}
              className="rounded-md border w-full"
              classNames={{
                months: "flex flex-col w-full",
                month: "space-y-6 w-full",
                caption: "flex justify-center pt-1 relative items-center",
                caption_label: "text-xl font-semibold hidden",
                nav: "hidden",
                nav_button: "hidden",
                nav_button_previous: "hidden",
                nav_button_next: "hidden",
                table: "w-full border-collapse",
                head_row: "flex w-full",
                head_cell: "text-muted-foreground rounded-md flex-1 font-semibold text-base py-3",
                row: "flex w-full mt-2",
                cell: "relative flex-1 p-1 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected])]:rounded-md",
                day: "h-14 w-full p-0 font-normal aria-selected:opacity-100 hover:bg-gray-100 rounded-md flex items-center justify-center text-base",
                day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                day_today: "bg-accent text-accent-foreground font-bold",
                day_outside: "text-muted-foreground opacity-50",
                day_disabled: "text-muted-foreground opacity-50",
                day_hidden: "invisible",
              }}
              modifiers={{
                hasEvent: (date) => hasEvents(date),
                holiday: (date) => !!getHolidayForDate(date)
              }}
              modifiersStyles={{
                hasEvent: { 
                  fontWeight: 'bold',
                  backgroundColor: '#e0e7ff',
                  borderRadius: '8px'
                },
                holiday: {
                  backgroundColor: '#fecdd3',
                  color: '#9f1239',
                  fontWeight: 'bold',
                  borderRadius: '8px'
                }
              }}
            />
          </CardContent>
        </Card>

        {/* Selected Date Events */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="text-lg">
                {selectedDate.toLocaleDateString('tr-TR', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </span>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => openNewModal(selectedDate)}
                className="gap-1"
              >
                <Plus className="h-3 w-3" />
                Ekle
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {getHolidayForDate(selectedDate) && (
              <div className="mb-3 px-3 py-2 rounded-md bg-rose-100 border border-rose-200 text-rose-800 text-sm font-medium" data-testid="accounting-holiday-banner">
                🎌 Resmi Tatil: {getHolidayForDate(selectedDate).name}{getHolidayForDate(selectedDate).day_type === 'half' ? ' (Yarım Gün)' : ''}
              </div>
            )}
            {selectedDateEvents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CalendarIcon className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                <p>Bu tarihte etkinlik yok</p>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedDateEvents.map((event) => (
                  <div 
                    key={event.id}
                    className={cn(
                      "p-3 rounded-lg border",
                      event.tamamlandi ? "bg-gray-50 border-gray-200" : 
                      event.tipi === 'alacak' ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {event.tipi === 'alacak' ? (
                            <TrendingUp className={cn("h-4 w-4", event.tamamlandi ? "text-gray-400" : "text-green-600")} />
                          ) : (
                            <TrendingDown className={cn("h-4 w-4", event.tamamlandi ? "text-gray-400" : "text-red-600")} />
                          )}
                          <span className={cn(
                            "font-semibold",
                            event.tamamlandi ? "text-gray-500 line-through" : 
                            event.tipi === 'alacak' ? "text-green-700" : "text-red-700"
                          )}>
                            {formatCurrency(event.miktar)}
                          </span>
                          {event.is_takip && (
                            <Badge variant="outline" className="text-xs">
                              Takip {event.odeme_sira}. Ödeme
                            </Badge>
                          )}
                          {event.is_cek && (
                            <Badge variant="outline" className={cn(
                              "text-xs",
                              event.cek_tip === 'alinan' ? "border-green-500 text-green-700" : "border-red-500 text-red-700"
                            )}>
                              {event.cek_tip === 'alinan' ? 'Alınan Çek' : 'Verilen Çek'}
                            </Badge>
                          )}
                          {event.is_kredi_karti && (
                            <Badge variant="outline" className="text-xs border-purple-500 text-purple-700">
                              💳 Kredi Kartı Ödeme
                            </Badge>
                          )}
                          {event.is_hatirlatici && (
                            <Badge 
                              variant="outline" 
                              className="text-xs"
                              style={{ borderColor: event.renk, color: event.renk }}
                            >
                              🔔 Hatırlatıcı
                            </Badge>
                          )}
                        </div>
                        {event.cari_hesap_adi && (
                          <p className="text-sm text-gray-600 font-medium">{event.cari_hesap_adi}</p>
                        )}
                        {event.is_hatirlatici && (
                          <div className="mt-2 space-y-1 text-xs text-gray-600 p-2 rounded border" style={{ backgroundColor: `${event.renk}10`, borderColor: event.renk }}>
                            {event.baslik && (
                              <p className="font-semibold" style={{ color: event.renk }}>{event.baslik}</p>
                            )}
                            {event.aciklama && (
                              <p>{event.aciklama}</p>
                            )}
                            {event.olusturan_adi && (
                              <p className="text-gray-400 mt-1">Oluşturan: {event.olusturan_adi}</p>
                            )}
                            {event.belgeler?.length > 0 && (
                              <div className="flex gap-1 flex-wrap mt-2">
                                {event.belgeler.map((belge, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs gap-1">
                                    <FileText className="h-3 w-3" />
                                    {belge.name}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        {event.is_kredi_karti && (
                          <div className="mt-2 space-y-1 text-xs text-gray-600 bg-purple-50 p-2 rounded border border-purple-200">
                            {event.banka_ismi && (
                              <p><span className="font-medium">Banka:</span> {event.banka_ismi}</p>
                            )}
                            {event.kart_basligi && (
                              <p><span className="font-medium">Kart:</span> {event.kart_basligi}</p>
                            )}
                            {event.kart_no && (
                              <p><span className="font-medium">Kart No:</span> {event.kart_no}</p>
                            )}
                            {event.fortis_subesi && (
                              <p><span className="font-medium">Şube:</span> {event.fortis_subesi}</p>
                            )}
                            {event.hesap_kesim_tarihi && (
                              <p><span className="font-medium">Hesap Kesim:</span> Ayın {event.hesap_kesim_tarihi}'i</p>
                            )}
                            {event.son_odeme_tarihi && (
                              <p><span className="font-medium">Son Ödeme:</span> Ayın {event.son_odeme_tarihi}'i</p>
                            )}
                            <p className="font-semibold text-purple-700 mt-1">
                              Dönem Harcama: {formatCurrency(event.donem_harcama || 0)}
                            </p>
                          </div>
                        )}
                        {event.is_cek && (
                          <div className="mt-2 space-y-1 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                            {event.cek_no && (
                              <p><span className="font-medium">Çek No:</span> {event.cek_no}</p>
                            )}
                            {event.banka && (
                              <p><span className="font-medium">Banka:</span> {event.banka}</p>
                            )}
                            {event.kesideci && (
                              <p><span className="font-medium">Keşideci:</span> {event.kesideci}</p>
                            )}
                            {event.alici && (
                              <p><span className="font-medium">Alıcı:</span> {event.alici}</p>
                            )}
                            {event.duzenleme_tarihi && (
                              <p><span className="font-medium">Düzenleme:</span> {new Date(event.duzenleme_tarihi).toLocaleDateString('tr-TR')}</p>
                            )}
                            {event.hesap_karti_adi && (
                              <p><span className="font-medium">Hesap:</span> {event.hesap_karti_adi}</p>
                            )}
                            {event.aciklama && (
                              <p><span className="font-medium">Açıklama:</span> {event.aciklama}</p>
                            )}
                          </div>
                        )}
                        {!event.is_cek && event.odeme_sekli && (
                          <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                            {getOdemeSekliIcon(event.odeme_sekli)}
                            <span>{getOdemeSekliLabel(event.odeme_sekli)}</span>
                          </div>
                        )}
                        {!event.is_cek && event.aciklama && (
                          <p className="text-xs text-gray-500 mt-1">{event.aciklama}</p>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        {!event.is_cek && !event.is_hatirlatici && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className={event.tamamlandi ? "text-gray-400" : "text-green-600 hover:text-green-700"}
                            onClick={() => handleToggleComplete(event.id, event.is_takip)}
                            title={event.tamamlandi ? "Aktifleştir" : "Tamamla"}
                          >
                            {event.tamamlandi ? <X className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                          </Button>
                        )}
                        {event.is_hatirlatici && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className={event.tamamlandi ? "text-gray-400" : "text-green-600 hover:text-green-700"}
                            onClick={() => handleToggleHatirlaticiComplete(event.hatirlatici_id, event.tarih)}
                            title={event.tamamlandi ? "Aktifleştir" : "Tamamla"}
                          >
                            {event.tamamlandi ? <X className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                          </Button>
                        )}
                        {!event.is_takip && !event.is_cek && !event.is_hatirlatici && !event.is_kredi_karti && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModal(event)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDelete(event.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {event.is_cek && (
                          <Badge variant="secondary" className="text-xs">
                            Vade
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-indigo-600" />
              {editingEvent ? 'Etkinlik Düzenle' : 'Yeni Etkinlik Ekle'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Alacak / Borç Seçimi */}
            <div className="space-y-2">
              <Label>Tür *</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={form.tipi === 'alacak' ? 'default' : 'outline'}
                  className={cn(
                    "flex-1 gap-2",
                    form.tipi === 'alacak' && "bg-green-600 hover:bg-green-700"
                  )}
                  onClick={() => setForm({ ...form, tipi: 'alacak' })}
                >
                  <TrendingUp className="h-4 w-4" />
                  Alacak
                </Button>
                <Button
                  type="button"
                  variant={form.tipi === 'borc' ? 'default' : 'outline'}
                  className={cn(
                    "flex-1 gap-2",
                    form.tipi === 'borc' && "bg-red-600 hover:bg-red-700"
                  )}
                  onClick={() => setForm({ ...form, tipi: 'borc' })}
                >
                  <TrendingDown className="h-4 w-4" />
                  Borç
                </Button>
              </div>
            </div>

            {/* Tarih */}
            <div className="space-y-2">
              <Label>Tarih *</Label>
              <Input
                type="date"
                value={form.tarih}
                onChange={(e) => setForm({ ...form, tarih: e.target.value })}
                data-testid="event-tarih"
              />
            </div>

            {/* Cari Hesap */}
            <div className="space-y-2">
              <Label>Cari Hesap</Label>
              <Popover open={cariOpen} onOpenChange={setCariOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={cariOpen}
                    className="w-full justify-between font-normal"
                    data-testid="event-cari-select"
                  >
                    {form.cari_hesap_adi || "Cari hesap seçin..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Cari hesap ara..." />
                    <CommandList>
                      <CommandEmpty>Cari hesap bulunamadı.</CommandEmpty>
                      <CommandGroup>
                        {cariHesaplar.map((cari) => (
                          <CommandItem
                            key={cari.id}
                            value={cari.ad}
                            onSelect={() => {
                              setForm({
                                ...form,
                                cari_hesap_id: cari.id,
                                cari_hesap_adi: cari.ad
                              });
                              setCariOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                form.cari_hesap_id === cari.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {cari.ad}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Etiket */}
            <div className="space-y-2">
              <Label>Etiket</Label>
              <Popover open={etiketOpen} onOpenChange={setEtiketOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={etiketOpen}
                    className="w-full justify-between font-normal"
                    data-testid="event-etiket-select"
                  >
                    {form.etiket_adi || "Etiket seçin..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Etiket ara..." />
                    <CommandList>
                      <CommandEmpty>Etiket bulunamadı.</CommandEmpty>
                      <CommandGroup>
                        {hesapKartlari.map((etiket) => (
                          <CommandItem
                            key={etiket.id}
                            value={etiket.kayit_adi}
                            onSelect={() => {
                              setForm({
                                ...form,
                                etiket_id: etiket.id,
                                etiket_adi: etiket.kayit_adi
                              });
                              setEtiketOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                form.etiket_id === etiket.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {etiket.kayit_adi}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Şube */}
            <div className="space-y-2">
              <Label>Fortis Şube</Label>
              <Select 
                value={form.fortis_sube_id} 
                onValueChange={(value) => {
                  const selectedBranch = branches.find(b => b.id === value);
                  setForm({
                    ...form,
                    fortis_sube_id: value,
                    fortis_sube: selectedBranch?.name || ''
                  });
                }}
              >
                <SelectTrigger data-testid="event-sube-select">
                  <SelectValue placeholder="Şube seçin..." />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Miktar */}
            <div className="space-y-2">
              <Label>Miktar (TL) *</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={form.miktar}
                onChange={(e) => setForm({ ...form, miktar: e.target.value })}
                data-testid="event-miktar"
              />
            </div>

            {/* Ödeme Şekli */}
            <div className="space-y-2">
              <Label>Ödeme Şekli</Label>
              <Select value={form.odeme_sekli} onValueChange={(v) => setForm({ ...form, odeme_sekli: v })}>
                <SelectTrigger data-testid="event-odeme-sekli">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="havale_eft">
                    <div className="flex items-center gap-2">
                      <Banknote className="h-4 w-4" />
                      Havale/EFT
                    </div>
                  </SelectItem>
                  <SelectItem value="cek">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Çek
                    </div>
                  </SelectItem>
                  <SelectItem value="nakit">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Nakit
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Açıklama */}
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea
                placeholder="Açıklama yazın..."
                value={form.aciklama}
                onChange={(e) => setForm({ ...form, aciklama: e.target.value })}
                data-testid="event-aciklama"
              />
            </div>

            {/* Belge Ekleme */}
            <div className="space-y-2">
              <Label>Belge Ekle</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleBelgeUpload}
                  className="flex-1"
                />
                {form.belge_url && (
                  <Badge variant="secondary" className="gap-1">
                    <Check className="h-3 w-3" />
                    Yüklendi
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleSubmit}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-event-btn"
            >
              {editingEvent ? 'Güncelle' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hatırlatıcı Ekleme/Düzenleme Modalı */}
      <Dialog open={showHatirlaticiModal} onOpenChange={setShowHatirlaticiModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-purple-600" />
              {editingHatirlatici ? 'Hatırlatıcı Düzenle' : 'Yeni Hatırlatıcı'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Başlık ve Açıklama */}
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label>İşin Adı / Başlık *</Label>
                <Input
                  value={hatirlaticiForm.baslik}
                  onChange={(e) => setHatirlaticiForm({...hatirlaticiForm, baslik: e.target.value})}
                  placeholder="Örn: SGK Bildirimi, KDV Beyannamesi..."
                  data-testid="reminder-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Açıklama / Not</Label>
                <Textarea
                  value={hatirlaticiForm.aciklama}
                  onChange={(e) => setHatirlaticiForm({...hatirlaticiForm, aciklama: e.target.value})}
                  placeholder="Detaylı açıklama yazın..."
                  rows={3}
                  data-testid="reminder-description-input"
                />
              </div>
            </div>

            {/* Renk Seçimi */}
            <div className="space-y-2">
              <Label>Renk</Label>
              <div className="flex gap-2 flex-wrap">
                {renkSecenekleri.map((renk) => (
                  <button
                    key={renk.value}
                    type="button"
                    onClick={() => setHatirlaticiForm({...hatirlaticiForm, renk: renk.value})}
                    className={cn(
                      "w-8 h-8 rounded-full border-2 transition-all",
                      hatirlaticiForm.renk === renk.value ? "border-gray-900 scale-110" : "border-transparent"
                    )}
                    style={{ backgroundColor: renk.value }}
                    title={renk.label}
                  />
                ))}
              </div>
            </div>

            {/* Yıl Seçimi */}
            <div className="space-y-2">
              <Label>Hangi Yıllarda?</Label>
              <div className="flex gap-2 flex-wrap">
                {yilSecenekleri.map((yil) => (
                  <Button
                    key={yil.value}
                    type="button"
                    variant={hatirlaticiForm.yillar.includes(yil.value) ? "default" : "outline"}
                    size="sm"
                    onClick={() => setHatirlaticiForm({
                      ...hatirlaticiForm, 
                      yillar: toggleArrayValue(hatirlaticiForm.yillar, yil.value)
                    })}
                  >
                    {yil.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Ay Seçimi */}
            <div className="space-y-2">
              <Label>Hangi Aylarda?</Label>
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  variant={hatirlaticiForm.aylar.includes('her_ay') ? "default" : "outline"}
                  size="sm"
                  onClick={() => setHatirlaticiForm({
                    ...hatirlaticiForm, 
                    aylar: hatirlaticiForm.aylar.includes('her_ay') ? [] : ['her_ay']
                  })}
                >
                  Her Ay
                </Button>
                {aylar.map((ay) => (
                  <Button
                    key={ay.value}
                    type="button"
                    variant={hatirlaticiForm.aylar.includes(ay.value) ? "default" : "outline"}
                    size="sm"
                    disabled={hatirlaticiForm.aylar.includes('her_ay')}
                    onClick={() => setHatirlaticiForm({
                      ...hatirlaticiForm, 
                      aylar: toggleArrayValue(hatirlaticiForm.aylar.filter(a => a !== 'her_ay'), ay.value)
                    })}
                  >
                    {ay.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Gün Tipi Seçimi */}
            <div className="space-y-2">
              <Label>Gün Seçim Tipi</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={hatirlaticiForm.gun_tipi === 'belirli' ? "default" : "outline"}
                  size="sm"
                  onClick={() => setHatirlaticiForm({...hatirlaticiForm, gun_tipi: 'belirli'})}
                >
                  Belirli Günler
                </Button>
                <Button
                  type="button"
                  variant={hatirlaticiForm.gun_tipi === 'haftalik' ? "default" : "outline"}
                  size="sm"
                  onClick={() => setHatirlaticiForm({...hatirlaticiForm, gun_tipi: 'haftalik'})}
                >
                  Haftanın Günleri
                </Button>
              </div>
            </div>

            {/* Belirli Günler */}
            {hatirlaticiForm.gun_tipi === 'belirli' && (
              <div className="space-y-2">
                <Label>Hangi Günlerde? (Ayın kaçı)</Label>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    type="button"
                    variant={hatirlaticiForm.gunler.includes('ilk_gun') ? "default" : "outline"}
                    size="sm"
                    onClick={() => setHatirlaticiForm({
                      ...hatirlaticiForm, 
                      gunler: toggleArrayValue(hatirlaticiForm.gunler, 'ilk_gun')
                    })}
                  >
                    İlk Gün
                  </Button>
                  <Button
                    type="button"
                    variant={hatirlaticiForm.gunler.includes('son_gun') ? "default" : "outline"}
                    size="sm"
                    onClick={() => setHatirlaticiForm({
                      ...hatirlaticiForm, 
                      gunler: toggleArrayValue(hatirlaticiForm.gunler, 'son_gun')
                    })}
                  >
                    Son Gün
                  </Button>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((gun) => (
                    <Button
                      key={gun}
                      type="button"
                      variant={hatirlaticiForm.gunler.includes(gun) ? "default" : "outline"}
                      size="sm"
                      className="w-10"
                      onClick={() => setHatirlaticiForm({
                        ...hatirlaticiForm, 
                        gunler: toggleArrayValue(hatirlaticiForm.gunler, gun)
                      })}
                    >
                      {gun}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Haftalık Günler */}
            {hatirlaticiForm.gun_tipi === 'haftalik' && (
              <div className="space-y-2">
                <Label>Haftanın Hangi Günlerinde?</Label>
                <div className="flex gap-2 flex-wrap">
                  {haftaGunleri.map((gun) => (
                    <Button
                      key={gun.value}
                      type="button"
                      variant={hatirlaticiForm.hafta_gunleri.includes(gun.value) ? "default" : "outline"}
                      size="sm"
                      onClick={() => setHatirlaticiForm({
                        ...hatirlaticiForm, 
                        hafta_gunleri: toggleArrayValue(hatirlaticiForm.hafta_gunleri, gun.value)
                      })}
                    >
                      {gun.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Belge Yükleme */}
            <div className="space-y-2">
              <Label>Belge Ekle (Opsiyonel)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      // Basit bir şekilde dosya adını sakla (gerçek uygulamada upload edilmeli)
                      setHatirlaticiForm({
                        ...hatirlaticiForm,
                        belgeler: [...hatirlaticiForm.belgeler, { name: file.name, url: URL.createObjectURL(file) }]
                      });
                    }
                  }}
                  className="flex-1"
                />
              </div>
              {hatirlaticiForm.belgeler.length > 0 && (
                <div className="flex gap-2 flex-wrap mt-2">
                  {hatirlaticiForm.belgeler.map((belge, index) => (
                    <Badge key={index} variant="secondary" className="gap-1">
                      <FileText className="h-3 w-3" />
                      {belge.name}
                      <button
                        type="button"
                        onClick={() => setHatirlaticiForm({
                          ...hatirlaticiForm,
                          belgeler: hatirlaticiForm.belgeler.filter((_, i) => i !== index)
                        })}
                        className="ml-1 hover:text-red-500"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHatirlaticiModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleHatirlaticiSubmit}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="save-reminder-btn"
            >
              {editingHatirlatici ? 'Güncelle' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hatırlatıcı Listesi Modalı */}
      <Dialog open={showHatirlaticiListModal} onOpenChange={setShowHatirlaticiListModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List className="h-5 w-5 text-purple-600" />
              Hatırlatıcı Listesi
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {hatirlaticilar.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bell className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Henüz hatırlatıcı eklenmemiş</p>
                <Button
                  onClick={() => {
                    setShowHatirlaticiListModal(false);
                    openHatirlaticiModal();
                  }}
                  className="mt-4 gap-2 bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="h-4 w-4" />
                  Hatırlatıcı Ekle
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {hatirlaticilar.map((hatirlatici) => (
                  <Card key={hatirlatici.id} className="overflow-hidden">
                    <div className="flex items-start gap-4 p-4">
                      <div 
                        className="w-3 h-full min-h-[60px] rounded-full flex-shrink-0"
                        style={{ backgroundColor: hatirlatici.renk || '#8B5CF6' }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="font-semibold text-gray-900">{hatirlatici.baslik}</h3>
                            {hatirlatici.aciklama && (
                              <p className="text-sm text-gray-500 mt-1">{hatirlatici.aciklama}</p>
                            )}
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setShowHatirlaticiListModal(false);
                                openHatirlaticiModal(hatirlatici);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteHatirlatici(hatirlatici.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <Badge variant="outline" className="text-xs">
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {hatirlatici.yillar?.includes('her_yil') ? 'Her yıl' : hatirlatici.yillar?.join(', ')}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {hatirlatici.aylar?.includes('her_ay') 
                              ? 'Her ay' 
                              : hatirlatici.aylar?.map(a => aylar.find(ay => ay.value === a)?.label).filter(Boolean).join(', ')
                            }
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {hatirlatici.gun_tipi === 'haftalik' 
                              ? hatirlatici.hafta_gunleri?.map(g => haftaGunleri.find(h => h.value === g)?.label).filter(Boolean).join(', ')
                              : hatirlatici.gunler?.map(g => g === 'ilk_gun' ? '1' : g === 'son_gun' ? 'Son' : g).join(', ') + '. gün'
                            }
                          </Badge>
                          {!hatirlatici.aktif && (
                            <Badge variant="secondary" className="text-xs">Pasif</Badge>
                          )}
                        </div>
                        {hatirlatici.belgeler?.length > 0 && (
                          <div className="flex gap-2 mt-2">
                            {hatirlatici.belgeler.map((belge, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs gap-1">
                                <FileText className="h-3 w-3" />
                                {belge.name}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHatirlaticiListModal(false)}>
              Kapat
            </Button>
            <Button 
              onClick={() => {
                setShowHatirlaticiListModal(false);
                openHatirlaticiModal();
              }}
              className="bg-purple-600 hover:bg-purple-700 gap-2"
            >
              <Plus className="h-4 w-4" />
              Yeni Hatırlatıcı
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountingCalendar;
